import "./parallax.scss";
import { useEffect, useRef, useState } from "react";

export default function Parallax({
  className = "",
  speed = 0.2,

  // OPTIONAL — visibility control
  hideWhenOutOfView = false,

  // existing opt-ins
  top = null,
  left = null,
  containerRef = null,
  containerSelector = "#root",
}) {
  const bgRef = useRef(null);
  const [isVisible, setIsVisible] = useState(true);

  // -----------------------------
  // VISIBILITY DETECTION
  // -----------------------------
  useEffect(() => {
    if (!hideWhenOutOfView || !bgRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Fully outside viewport
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0,
      }
    );

    observer.observe(bgRef.current);

    return () => observer.disconnect();
  }, [hideWhenOutOfView]);

  // -----------------------------
  // PARALLAX LOGIC (UNCHANGED)
  // -----------------------------
  useEffect(() => {
    const container =
      containerRef?.current || document.querySelector(containerSelector);

    if (!container || !bgRef.current) return;

    const el = bgRef.current;
    const useAdvancedPositioning = top !== null || left !== null;

    // Legacy mode
    if (!useAdvancedPositioning) {
      const handleScroll = () => {
        const offset = container.scrollTop * speed;
        el.style.transform = `translateY(${offset}px)`;
      };

      handleScroll();
      container.addEventListener("scroll", handleScroll, { passive: true });

      return () => container.removeEventListener("scroll", handleScroll);
    }

    // Advanced mode
    el.style.setProperty("--parallax-top", `${top ?? 0}px`);
    el.style.setProperty("--parallax-left", `${left ?? 0}px`);

    const handleScroll = () => {
      const offsetY = container.scrollTop * speed;
      el.style.setProperty("--parallax-offset-y", `${offsetY}px`);
    };

    handleScroll();
    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => container.removeEventListener("scroll", handleScroll);
  }, [speed, top, left, containerRef, containerSelector]);

  return (
    <div
      ref={bgRef}
      className={`parallax ${className} ${
        hideWhenOutOfView && !isVisible ? "parallax--hidden" : ""
      }`}
    />
  );
}
